import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {Alert, Button, Col, Container, Form, Modal, Row, Spinner} from 'react-bootstrap';
import {FaArrowLeft, FaCalendarAlt, FaCheck, FaChevronRight, FaClock, FaSave, FaUsers} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import CommonMetaTag from './common-meta-tag';
import {
  createOpenMatch,
  createPartnerOpenMatch,
  getFieldSlots,
  getPartnerFields,
  getPartners,
  getPlayerSportRankings,
  quoteOpenMatch,
  quotePartnerOpenMatch,
  savePlayerProfile,
  savePlayerSportRankingQuestionnaire
} from '../api';
import {
  mergeRankingQuestionnaires,
  normalizePlayerRankings,
  playerRankingQuestionnaires,
  rankingOptionLabel,
  rankingQuestionLabel,
  upsertPlayerRanking
} from '../constants/player-ranking-questionnaires';
import {ROLES} from '../constants';

const SPORT_LABELS = {
  tennis: 'SPORT_TENNIS',
  padel: 'SPORT_PADEL',
  pickleball: 'SPORT_PICKLEBALL'
};

const GENDER_LABELS = {
  any: 'OPEN_MATCH_GENDER_ANY',
  male: 'OPEN_MATCH_GENDER_MALE',
  female: 'OPEN_MATCH_GENDER_FEMALE',
  mixed: 'OPEN_MATCH_GENDER_MIXED'
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);
const currentHourInputValue = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:00`;
};
const slotTimeValue = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};
const minutesFromTime = time => {
  const [hours, minutes] = String(time || '').split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};
const timeOptionsFromSlots = slots => Array.from(new Set(
  slots.map(slot => slotTimeValue(slot.startDatetime)).filter(Boolean)
)).sort();
const closestTimeOption = (options, preferredTime) => {
  if (!options.length) return preferredTime || currentHourInputValue();
  const targetMinutes = minutesFromTime(preferredTime) ?? minutesFromTime(currentHourInputValue());
  return options.reduce((closest, option) => {
    const closestDistance = Math.abs((minutesFromTime(closest) ?? 0) - targetMinutes);
    const optionDistance = Math.abs((minutesFromTime(option) ?? 0) - targetMinutes);
    return optionDistance < closestDistance ? option : closest;
  }, options[0]);
};

const normalizeDateInput = value => {
  if (!value) return todayInputValue();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return todayInputValue();
  return parsed.toISOString().slice(0, 10);
};

const formatDate = (value, locale = 'it-IT') => {
  if (!value) return '';
  return new Date(value).toLocaleDateString(locale, {weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'});
};

const formatTime = value => {
  if (!value) return '';
  return new Date(value).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const formatMoney = cents => `${(Number(cents || 0) / 100).toFixed(2)} €`;

const sportValue = value => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.name || value.label || value.value || value.sport || value.type || '';
  }
  return String(value);
};
const normalizeSport = value => sportValue(value)
  .toLowerCase()
  .trim()
  .replace(/^sport[-_\s]*/, '')
  .replace(/^replayer[-_\s]*/, '');
const sportDisplayName = (sport, t) => {
  const normalized = normalizeSport(sport);
  if (normalized === 'tennis') return 'Tennis';
  if (normalized === 'padel') return 'Padel';
  if (normalized === 'pickleball') return 'Pickleball';
  return sportValue(sport) || normalized || t('SPORT');
};

const slotId = slot => slot.slotId || slot.id;
const filterSlotsAroundTime = (slots, date, time) => {
  if (!time) return slots;
  const targetTime = new Date(`${date}T${time}:00`).getTime();
  if (!Number.isFinite(targetTime)) return slots;
  const windowSizeMs = 60 * 60 * 1000;
  return [...slots]
    .filter(slot => {
      const slotTime = new Date(slot.startDatetime).getTime();
      return Number.isFinite(slotTime) && slotTime >= targetTime - windowSizeMs && slotTime <= targetTime + windowSizeMs;
    })
    .sort((firstSlot, secondSlot) => new Date(firstSlot.startDatetime) - new Date(secondSlot.startDatetime));
};

const hasOpenMatchFlag = partner => (
  Object.prototype.hasOwnProperty.call(partner || {}, 'openMatchesEnabled') ||
  Object.prototype.hasOwnProperty.call(partner || {}, 'open_matches_enabled')
);

const isOpenMatchEnabledPartner = partner => {
  if (!hasOpenMatchFlag(partner)) return true;
  return partner.openMatchesEnabled === true || partner.open_matches_enabled === true;
};

const rankingLevelValue = ranking => {
  const value = ranking && (ranking.displayLevel || ranking.level || ranking.rating);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const rankingLevelLabel = ranking => {
  const value = rankingLevelValue(ranking);
  return value !== null ? `Level ${value.toFixed(1)}` : null;
};

const formatLevelInput = value => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(1) : '';
};

const isAvailableSlot = slot => {
  if (!slot || !slot.startDatetime) return false;
  if (new Date(slot.startDatetime) <= new Date()) return false;
  if (slot.openMatch) return false;
  if (slot.state && slot.state !== 'available') return false;
  return true;
};

const levelText = (draft, quote, t) => {
  if (draft.levelRangeMode === 'any') return t('OPEN_MATCH_LEVEL_ANY');
  if (draft.levelRangeMode === 'custom') return t('OPEN_MATCH_LEVEL_CUSTOM_VALUE', {min: draft.minLevel || '-', max: draft.maxLevel || '-'});
  if (quote && quote.minLevel !== null && quote.maxLevel !== null) return t('OPEN_MATCH_LEVEL_SIMILAR_VALUE', {min: Number(quote.minLevel).toFixed(1), max: Number(quote.maxLevel).toFixed(1)});
  return t('OPEN_MATCH_LEVEL_SIMILAR');
};

const errorMessage = (error, t, fallbackKey = 'OPEN_MATCH_OPERATION_FAILED') => {
  if (!error) return t(fallbackKey);
  const key = {
    ranking_required_for_sport: 'OPEN_MATCH_ERROR_RANKING_REQUIRED_FOR_SPORT',
    slot_not_available_for_open_match: 'OPEN_MATCH_ERROR_SLOT_NOT_AVAILABLE',
    open_match_already_exists_for_slot: 'OPEN_MATCH_ERROR_ALREADY_EXISTS',
    not_enough_available_credit: 'OPEN_MATCH_ERROR_NOT_ENOUGH_CREDIT',
    gender_required: 'OPEN_MATCH_ERROR_GENDER_REQUIRED',
    gender_not_allowed: 'OPEN_MATCH_ERROR_GENDER_NOT_ALLOWED',
    mixed_open_match_requires_doubles: 'OPEN_MATCH_ERROR_MIXED_REQUIRES_DOUBLES',
    mixed_gender_quota_full: 'OPEN_MATCH_ERROR_MIXED_GENDER_QUOTA_FULL',
    partner_level_range_required: 'PARTNER_OPEN_MATCH_LEVEL_REQUIRED',
    partner_slot_required: 'PARTNER_OPEN_MATCH_SLOT_REQUIRED',
    open_matches_not_enabled_for_partner: 'PARTNER_OPEN_MATCH_NOT_ENABLED'
  }[error];
  return t(key || fallbackKey);
};

const OpenMatchCreateWizard = ({token, profile, history, location}) => {
  const {t, i18n} = useTranslation();
  const locale = i18n.language || 'it-IT';
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const initialPartnerId = params.get('partnerId') || '';
  const initialSport = normalizeSport(params.get('sport'));
  const initialSlotId = params.get('slotId') || '';
  const initialDate = normalizeDateInput(params.get('date'));
  const rematchMode = params.get('rematch') === '1';
  const partnerContext = useMemo(() => {
    const partnerMode = Boolean(profile && profile.user && profile.user.role === ROLES.partner);
    return {
      isPartner: partnerMode,
      partnerProfileId: partnerMode ? String(profile.uid || profile.user.id || profile.user.uid || '') : '',
      partnerRoute: (location.pathname || '').indexOf('/partner-open-matches') === 0,
      lockPartner: partnerMode || (Boolean(initialPartnerId) && !rematchMode),
      lockSport: Boolean(initialSport) && !rematchMode
    };
  }, [initialPartnerId, initialSport, location.pathname, profile, rematchMode]);
  const {isPartner, partnerProfileId, partnerRoute, lockPartner, lockSport} = partnerContext;
  const [step, setStep] = useState(1);
  const [partners, setPartners] = useState([]);
  const [fields, setFields] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slots, setSlots] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [questionnaires, setQuestionnaires] = useState(playerRankingQuestionnaires);
  const [selectedPartnerId, setSelectedPartnerId] = useState(initialPartnerId);
  const [selectedSport, setSelectedSport] = useState(initialSport);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(currentHourInputValue());
  const [selectedSlotId, setSelectedSlotId] = useState(initialSlotId);
  const [initialSlotTimeApplied, setInitialSlotTimeApplied] = useState(false);
  const [draft, setDraft] = useState({
    matchType: 'friendly',
    playersRequired: 4,
    gender: 'any',
    levelRangeMode: 'similar',
    minLevel: '',
    maxLevel: '',
    title: '',
    notes: ''
  });
  const [quote, setQuote] = useState(null);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);
  const [rankingModalOpen, setRankingModalOpen] = useState(false);
  const [rankingAnswers, setRankingAnswers] = useState({});
  const [rankingSavedResult, setRankingSavedResult] = useState(null);
  const [genderModalOpen, setGenderModalOpen] = useState(false);
  const [genderChoice, setGenderChoice] = useState((profile && profile.gender) || '');
  const [localGender, setLocalGender] = useState((profile && profile.gender) || '');
  const [savingGender, setSavingGender] = useState(false);
  const [savingRanking, setSavingRanking] = useState(false);
  const createInFlightRef = useRef(false);

  const selectedPartner = partners.find(partner => String(partner.uid || partner.id) === String(selectedPartnerId));
  const sportFields = useMemo(() => (
    fields.filter(field => normalizeSport(field.sport) === selectedSport)
  ), [fields, selectedSport]);
  const selectedSlot = slots.find(slot => String(slotId(slot)) === String(selectedSlotId));
  const selectedField = selectedSlot ? fields.find(field => Number(field.id) === Number(selectedSlot.fieldId)) : null;
  const selectedRanking = rankings.find(ranking => ranking.sport === selectedSport);
  const selectedQuestionnaire = questionnaires[selectedSport] || {questions: [], options: []};
  const contextComplete = selectedPartnerId && selectedSport;
  const profileGender = localGender || (profile && profile.gender) || '';

  useEffect(() => {
    setLocalGender((profile && profile.gender) || '');
    setGenderChoice((profile && profile.gender) || '');
  }, [profile]);

  useEffect(() => {
    if (!isPartner || !partnerProfileId) return;
    setSelectedPartnerId(partnerProfileId);
    setPartners([profile]);
    setDraft(current => ({...current, levelRangeMode: 'custom'}));
  }, [isPartner, partnerProfileId, profile]);

  const setCustomLevelRange = () => {
    const level = rankingLevelValue(selectedRanking);
    setDraft(current => ({
      ...current,
      levelRangeMode: 'custom',
      minLevel: current.minLevel !== '' ? current.minLevel : formatLevelInput(level !== null ? Math.max(1, level - 0.8) : null),
      maxLevel: current.maxLevel !== '' ? current.maxLevel : formatLevelInput(level !== null ? Math.min(10, level + 0.8) : null)
    }));
  };

  const openRankingModal = () => {
    setRankingAnswers({});
    setRankingSavedResult(null);
    setGenderChoice(profileGender || '');
    setRankingModalOpen(true);
  };

  const openGenderModal = () => {
    setGenderChoice(profileGender || '');
    setGenderModalOpen(true);
  };

  const loadRankings = useCallback(async () => {
    if (!token || isPartner) return;
    const res = await getPlayerSportRankings(token);
    if (res.status === 200) {
      setRankings(normalizePlayerRankings(res.json.rankings));
      setQuestionnaires(mergeRankingQuestionnaires(res.json.questionnaires));
    }
  }, [isPartner, token]);

  useEffect(() => {
    const loadPartners = async () => {
      if (!token) return;
      if (isPartner) {
        setPartners(profile ? [profile] : []);
        return;
      }
      setLoadingPartners(true);
      const res = await getPartners(token);
      if (res.status === 200) {
        setPartners((res.json || []).filter(isOpenMatchEnabledPartner));
      } else {
        setMessage(t('OPEN_MATCH_CREATE_LOAD_CLUBS_FAILED'));
      }
      setLoadingPartners(false);
    };
    loadPartners();
    loadRankings();
  }, [isPartner, loadRankings, profile, t, token]);

  useEffect(() => {
    const loadFields = async () => {
      if (!selectedPartnerId || !token) return;
      setLoadingFields(true);
      setFields([]);
      setAvailableSlots([]);
      setSlots([]);
      setSelectedSlotId('');
      const res = await getPartnerFields(selectedPartnerId, token);
      if (res.status === 200) {
        setFields((res.json || []).filter(field => field.bookable !== false && field.visible !== false));
      } else {
        setMessage(t('OPEN_MATCH_CREATE_LOAD_FIELDS_FAILED'));
      }
      setLoadingFields(false);
    };
    loadFields();
  }, [selectedPartnerId, t, token]);

  const loadSlots = useCallback(async () => {
    if (!contextComplete || !token) return;
    setLoadingSlots(true);
    setMessage(null);
    const results = await Promise.all(sportFields.map(field => getFieldSlots(selectedPartnerId, token, field.id, false, new Date(`${date}T12:00:00`), 'DAY')));
    const nextSlots = [];
    results.forEach((res, index) => {
      if (res.status === 200 && Array.isArray(res.json)) {
        res.json.forEach(slot => nextSlots.push({...slot, fieldId: slot.fieldId || sportFields[index].id}));
      }
    });
    nextSlots.sort((a, b) => new Date(a.startDatetime) - new Date(b.startDatetime));
    const allAvailableSlots = nextSlots.filter(isAvailableSlot);
    const nextAvailableSlots = filterSlotsAroundTime(allAvailableSlots, date, time);
    setAvailableSlots(allAvailableSlots);
    setSlots(nextAvailableSlots);
    setSelectedSlotId(currentSlotId => (
      currentSlotId && !nextAvailableSlots.some(slot => String(slotId(slot)) === String(currentSlotId)) ? '' : currentSlotId
    ));
    setLoadingSlots(false);
  }, [contextComplete, date, selectedPartnerId, sportFields, time, token]);

  useEffect(() => {
    if (contextComplete) {
      loadSlots();
    }
  }, [contextComplete, loadSlots]);

  const timeOptions = useMemo(() => timeOptionsFromSlots(availableSlots), [availableSlots]);

  useEffect(() => {
    if (initialSlotTimeApplied || !initialSlotId || availableSlots.length === 0) return;
    const initialSlot = availableSlots.find(slot => String(slotId(slot)) === String(initialSlotId));
    if (!initialSlot) {
      setInitialSlotTimeApplied(true);
      return;
    }
    setTime(slotTimeValue(initialSlot.startDatetime));
    setInitialSlotTimeApplied(true);
  }, [availableSlots, initialSlotId, initialSlotTimeApplied]);

  useEffect(() => {
    if (!contextComplete || timeOptions.length === 0 || (initialSlotId && !initialSlotTimeApplied)) return;
    setTime(currentTime => (
      timeOptions.includes(currentTime) ? currentTime : closestTimeOption(timeOptions, currentTime)
    ));
  }, [contextComplete, initialSlotId, initialSlotTimeApplied, timeOptions]);

  const buildPayload = () => ({
    slotId: Number(selectedSlotId),
    matchType: draft.matchType,
    playersRequired: Number(draft.playersRequired),
    gender: draft.gender,
    levelRangeMode: draft.levelRangeMode,
    minLevel: draft.levelRangeMode === 'custom' && draft.minLevel !== '' ? Number(draft.minLevel) : null,
    maxLevel: draft.levelRangeMode === 'custom' && draft.maxLevel !== '' ? Number(draft.maxLevel) : null,
    title: draft.title,
    notes: draft.notes
  });

  const ensureRequirementsAndContinue = () => {
    if (!selectedSlotId) {
      setMessage(t('OPEN_MATCH_CREATE_CHOOSE_SLOT_FIRST'));
      return;
    }
    if (isPartner) {
      setStep(2);
      return;
    }
    if (!selectedRanking || !selectedRanking.configured) {
      openRankingModal();
      return;
    }
    if (!profileGender) {
      openGenderModal();
      return;
    }
    setStep(2);
  };

  const loadQuote = async () => {
    if (!selectedSlotId) {
      setMessage(t('OPEN_MATCH_CREATE_CHOOSE_SLOT_FIRST'));
      return;
    }
    if (isPartner && (draft.minLevel === '' || draft.maxLevel === '')) {
      setMessage(t('PARTNER_OPEN_MATCH_LEVEL_REQUIRED'));
      return;
    }
    setLoadingQuote(true);
    setMessage(null);
    const res = isPartner
      ? await quotePartnerOpenMatch(buildPayload(), token)
      : await quoteOpenMatch(buildPayload(), token);
    if (res.status === 200) {
      setQuote(res.json.quote);
      setStep(3);
    } else if (res.json && res.json.error === 'ranking_required_for_sport') {
      openRankingModal();
    } else if (res.json && res.json.error === 'gender_required') {
      openGenderModal();
    } else {
      setMessage(errorMessage(res.json && res.json.error, t, 'OPEN_MATCH_CREATE_QUOTE_FAILED'));
    }
    setLoadingQuote(false);
  };

  const saveRanking = async () => {
    const allAnswered = selectedQuestionnaire.questions.every(question => rankingAnswers[question.key] !== undefined);
    if (!allAnswered) {
      setMessage(t('RANKING_COMPLETE_ALL_QUESTIONS'));
      return;
    }
    if (!profileGender && !genderChoice) {
      setMessage(t('OPEN_MATCH_CREATE_SELECT_GENDER'));
      return;
    }

    setSavingRanking(true);
    if (!profileGender && genderChoice) {
      const profileRes = await savePlayerProfile({...profile, gender: genderChoice}, token);
      if (profileRes.status !== 200) {
        setMessage(errorMessage(profileRes.json && profileRes.json.error, t, 'OPEN_MATCH_CREATE_SAVE_GENDER_FAILED'));
        setSavingRanking(false);
        return;
      }
      setLocalGender(genderChoice);
    }
    const answers = selectedQuestionnaire.questions.map(question => ({questionKey: question.key, value: rankingAnswers[question.key]}));
    const res = await savePlayerSportRankingQuestionnaire(selectedSport, answers, token);
    if (res.status === 200 && res.json && res.json.ranking) {
      setRankings(current => upsertPlayerRanking(current, res.json.ranking));
      setRankingSavedResult(res.json.ranking);
      setRankingAnswers({});
    } else {
      setMessage(errorMessage(res.json && res.json.error, t, 'RANKING_SAVE_FAILED_GENERIC'));
    }
    setSavingRanking(false);
  };

  const saveGenderAndContinue = async () => {
    if (!genderChoice) {
      setMessage(t('OPEN_MATCH_CREATE_SELECT_GENDER_SHORT'));
      return;
    }
    setSavingGender(true);
    const res = await savePlayerProfile({...profile, gender: genderChoice}, token);
    if (res.status === 200) {
      setLocalGender(genderChoice);
      setGenderModalOpen(false);
      if (step === 1 && selectedSlotId) {
        setStep(2);
      }
    } else {
      setMessage(errorMessage(res.json && res.json.error, t, 'OPEN_MATCH_CREATE_SAVE_GENDER_FAILED'));
    }
    setSavingGender(false);
  };

  const createMatch = async () => {
    if (createInFlightRef.current) return;
    createInFlightRef.current = true;
    setCreating(true);
    setMessage(null);
    const res = isPartner
      ? await createPartnerOpenMatch(buildPayload(), token)
      : await createOpenMatch(buildPayload(), token);
    if (res.status === 201 && res.json && res.json.openMatch && res.json.openMatch.publicId) {
      history.push(`/matches/${res.json.openMatch.publicId}`);
      return;
    }
    if (res.json && res.json.error === 'ranking_required_for_sport') {
      openRankingModal();
    } else if (res.json && res.json.error === 'gender_required') {
      openGenderModal();
    } else {
      setMessage(errorMessage(res.json && res.json.error, t, 'OPEN_MATCH_CREATE_FAILED'));
    }
    createInFlightRef.current = false;
    setCreating(false);
  };

  if (!token || !profile) {
    return <Redirect to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}/>;
  }
  if (partnerRoute && (!isPartner || !profile.openMatchesEnabled)) {
    return <Redirect to={isPartner ? '/partner-fields' : '/'}/>;
  }

  return (
    <Container className="open-match-wizard-page">
      <CommonMetaTag/>
      <header className="open-match-v1-hero">
        <button type="button" onClick={() => history.goBack()}><FaArrowLeft aria-hidden="true"/>{t('BACK')}</button>
        <span><FaUsers aria-hidden="true"/> {t('OPEN_MATCHES')}</span>
        <h1>{t('CREATE_MATCH')}</h1>
        <p>{t(isPartner ? 'PARTNER_OPEN_MATCH_CREATE_HERO_TEXT' : 'OPEN_MATCH_CREATE_HERO_TEXT')}</p>
      </header>

      {message ? <Alert variant="info" className="open-match-v1-alert">{message}</Alert> : null}

      <div className="open-match-v1-steps">
        {[t('OPEN_MATCH_STEP_WHEN'), t('OPEN_MATCH_STEP_HOW'), t('CONFIRM')].map((label, index) => (
          <span key={label} className={step === index + 1 ? 'active' : step > index + 1 ? 'done' : ''}>{step > index + 1 ? <FaCheck aria-hidden="true"/> : index + 1} {label}</span>
        ))}
      </div>

      {step === 1 ? (
        <section className="open-match-v1-card">
          <h2>{t('OPEN_MATCH_CREATE_WHEN_TITLE')}</h2>
          <Row className="open-match-v1-when-grid">
            {!lockPartner ? (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('CLUB')}</Form.Label>
                  <Form.Control as="select" value={selectedPartnerId} disabled={loadingPartners} onChange={event => {
                    setSelectedPartnerId(event.target.value);
                    setSelectedSport('');
                  }}>
                    <option value="">{t('CHOOSE_CLUB')}</option>
                    {partners.map(partner => <option key={partner.uid || partner.id} value={partner.uid || partner.id}>{partner.name || partner.businessName}</option>)}
                  </Form.Control>
                </Form.Group>
              </Col>
            ) : null}
            {!lockSport ? (
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('SPORT')}</Form.Label>
                  <Form.Control as="select" value={selectedSport} disabled={!selectedPartnerId || loadingFields} onChange={event => setSelectedSport(event.target.value)}>
                    <option value="">{t('CHOOSE_SPORT')}</option>
                    {Object.entries(SPORT_LABELS).filter(([sport]) => fields.some(field => normalizeSport(field.sport) === sport)).map(([sport]) => (
                      <option key={sport} value={sport}>{sportDisplayName(sport, t)}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            ) : null}
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('DATE')}</Form.Label>
                <Form.Control className="open-match-v1-date-control" type="date" value={date} min={todayInputValue()} onChange={event => setDate(event.target.value)}/>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label><FaClock aria-hidden="true"/> {t('TIME')}</Form.Label>
                <Form.Control
                  className="open-match-v1-time-control"
                  as="select"
                  value={time}
                  disabled={!contextComplete || timeOptions.length === 0}
                  onChange={event => setTime(event.target.value)}
                >
                  {
                    timeOptions.length === 0 ?
                      <option value={time}>{time || currentHourInputValue()}</option> :
                      timeOptions.map(option => <option key={option} value={option}>{option}</option>)
                  }
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <div className="open-match-v1-slot-list">
            {loadingSlots ? <div className="open-match-v1-loading"><Spinner animation="border" size="sm"/>{t('OPEN_MATCH_CREATE_LOADING_SLOTS')}</div> : null}
            {!loadingSlots && contextComplete && slots.length === 0 ? <div className="open-match-v1-empty">{t('OPEN_MATCH_CREATE_NO_SLOTS')}</div> : null}
            {slots.map(slot => {
              const field = fields.find(item => Number(item.id) === Number(slot.fieldId));
              return (
                <button key={slotId(slot)} type="button" className={String(selectedSlotId) === String(slotId(slot)) ? 'active' : ''} onClick={() => setSelectedSlotId(String(slotId(slot)))}>
                  <FaCalendarAlt aria-hidden="true"/>
                      <span>{formatTime(slot.startDatetime)} - {formatTime(slot.endDatetime)}</span>
                      <small>
                        {field ? field.name : t('FIELD')}
                        {field && field.sport ? (
                          <>
                            {' · '}
                            <span className="sport-slot-meta">{sportDisplayName(field.sport, t)}</span>
                          </>
                        ) : null}
                      </small>
                </button>
              );
            })}
          </div>
          <div className="open-match-v1-footer">
            <Button disabled={!selectedSlotId} className="open-match-v1-primary" onClick={ensureRequirementsAndContinue}>{t('NEXT')} <FaChevronRight aria-hidden="true"/></Button>
          </div>
        </section>
      ) : null}

      {step === 2 ? (
        <section className="open-match-v1-card">
          <h2>{t('OPEN_MATCH_CREATE_HOW_TITLE')}</h2>
          <Form.Group>
            <Form.Label>{t('OPEN_MATCH_FORMAT')}</Form.Label>
            <div className="open-match-v1-segment">
              <button type="button" className={Number(draft.playersRequired) === 2 ? 'active' : ''} onClick={() => setDraft({...draft, playersRequired: 2, gender: draft.gender === 'mixed' ? 'any' : draft.gender})}>{t('OPEN_MATCH_FORMAT_SINGLE')}</button>
              <button type="button" className={Number(draft.playersRequired) === 4 ? 'active' : ''} onClick={() => setDraft({...draft, playersRequired: 4})}>{t('OPEN_MATCH_FORMAT_DOUBLE')}</button>
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('OPEN_MATCH_GENDER')}</Form.Label>
            <div className="open-match-v1-segment open-match-v1-segment--four">
              {Object.entries(GENDER_LABELS).map(([gender, labelKey]) => (
                <button
                  key={gender}
                  type="button"
                  className={draft.gender === gender ? 'active' : ''}
                  onClick={() => setDraft({...draft, gender, playersRequired: gender === 'mixed' ? 4 : draft.playersRequired})}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          </Form.Group>
          {draft.gender === 'mixed' ? <Alert variant="info" className="open-match-v1-field-note">{t('OPEN_MATCH_MIXED_HINT')}</Alert> : null}
          <Form.Group>
            <Form.Label>{t('TYPE')}</Form.Label>
            <div className="open-match-v1-segment">
              <button type="button" className={draft.matchType === 'friendly' ? 'active' : ''} onClick={() => setDraft({...draft, matchType: 'friendly'})}>{t('OPEN_MATCH_TYPE_FRIENDLY')}</button>
              <button type="button" className={draft.matchType === 'competitive' ? 'active' : ''} onClick={() => setDraft({...draft, matchType: 'competitive'})}>{t('OPEN_MATCH_TYPE_COMPETITIVE')}</button>
            </div>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('LEVEL')}</Form.Label>
            {isPartner ? <p className="open-match-ranking-intro">{t('PARTNER_OPEN_MATCH_LEVEL_HELP')}</p> : (
              <div className="open-match-v1-segment open-match-v1-segment--three">
                <button type="button" className={draft.levelRangeMode === 'similar' ? 'active' : ''} onClick={() => setDraft({...draft, levelRangeMode: 'similar'})}>{t('OPEN_MATCH_LEVEL_SIMILAR')}</button>
                <button type="button" className={draft.levelRangeMode === 'any' ? 'active' : ''} onClick={() => setDraft({...draft, levelRangeMode: 'any'})}>{t('OPEN_MATCH_LEVEL_ANY')}</button>
                <button type="button" className={draft.levelRangeMode === 'custom' ? 'active' : ''} onClick={setCustomLevelRange}>{t('CUSTOM')}</button>
              </div>
            )}
          </Form.Group>
          {draft.levelRangeMode === 'custom' || isPartner ? (
            <Row>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>{t('OPEN_MATCH_MIN_LEVEL')}</Form.Label>
                  <Form.Control type="number" min="1" max="10" step="0.1" value={draft.minLevel} onChange={event => setDraft({...draft, minLevel: event.target.value})}/>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group>
                  <Form.Label>{t('OPEN_MATCH_MAX_LEVEL')}</Form.Label>
                  <Form.Control type="number" min="1" max="10" step="0.1" value={draft.maxLevel} onChange={event => setDraft({...draft, maxLevel: event.target.value})}/>
                </Form.Group>
              </Col>
            </Row>
          ) : null}
          <Form.Group>
            <Form.Label>{t('TITLE')}</Form.Label>
            <Form.Control value={draft.title} maxLength={120} placeholder={t('OPEN_MATCH_TITLE_PLACEHOLDER')} onChange={event => setDraft({...draft, title: event.target.value})}/>
          </Form.Group>
          <Form.Group>
            <Form.Label>{t('NOTES')}</Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={500} value={draft.notes} placeholder={t('OPEN_MATCH_NOTES_PLACEHOLDER')} onChange={event => setDraft({...draft, notes: event.target.value})}/>
          </Form.Group>
          <div className="open-match-v1-footer">
            <Button variant="outline-secondary" onClick={() => setStep(1)}>{t('BACK')}</Button>
            <Button className="open-match-v1-primary" disabled={loadingQuote} onClick={loadQuote}>{loadingQuote ? t('OPEN_MATCH_CALCULATING_PRICE') : t('OPEN_MATCH_GO_TO_SUMMARY')}</Button>
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="open-match-v1-card open-match-v1-summary">
          <h2>{t('CONFIRM')}</h2>
          <div className="open-match-v1-summary-grid">
            <div><span>{t('SPORT')}</span><strong>{sportDisplayName(selectedSport, t)}</strong></div>
            <div><span>{t('CLUB')}</span><strong>{selectedPartner ? selectedPartner.name || selectedPartner.businessName : '-'}</strong></div>
            <div><span>{t('WHEN')}</span><strong>{formatDate(selectedSlot && selectedSlot.startDatetime, locale)} · {formatTime(selectedSlot && selectedSlot.startDatetime)}-{formatTime(selectedSlot && selectedSlot.endDatetime)}</strong></div>
            <div><span>{t('FIELD')}</span><strong>{selectedField ? selectedField.name : '-'}</strong></div>
            <div><span>{t('OPEN_MATCH_FORMAT')}</span><strong>{Number(draft.playersRequired) === 2 ? t('OPEN_MATCH_FORMAT_SINGLE') : t('OPEN_MATCH_FORMAT_DOUBLE')} · {draft.matchType === 'competitive' ? t('OPEN_MATCH_TYPE_COMPETITIVE') : t('OPEN_MATCH_TYPE_FRIENDLY')}</strong></div>
            <div><span>{t('OPEN_MATCH_GENDER')}</span><strong>{t(GENDER_LABELS[draft.gender] || 'OPEN_MATCH_GENDER_ANY')}</strong></div>
            <div><span>{t('LEVEL')}</span><strong>{levelText(draft, quote, t)}</strong></div>
            <div><span>{t('PLAYERS')}</span><strong>{isPartner ? 0 : 1}/{draft.playersRequired}</strong></div>
            <div><span>{t('OPEN_MATCH_ORGANIZER')}</span><strong>{isPartner ? (profile.name || profile.businessName || t('CLUB')) : `${profile.name || ''} ${profile.surname || ''}`.trim() || t('YOU')}</strong>{!isPartner ? ` · ${rankingLevelLabel(selectedRanking) || t('OPEN_MATCH_LEVEL_TO_COMPLETE')}` : ''}</div>
            <div><span>{t('OPEN_MATCH_PARTICIPANT_FEE')}</span><strong>{quote ? formatMoney(quote.participantPriceCents) : '-'}</strong></div>
          </div>
          <Alert variant="info" className="open-match-v1-field-note">
            {quote && quote.requiresOnlinePayment === false
              ? t('OPEN_MATCH_FIELD_CONFIRMED_PAY_AT_CLUB')
              : t('OPEN_MATCH_FIELD_CONFIRMED_HOLD')}
          </Alert>
          {quote && quote.canReserve === false ? <Alert variant="warning">{errorMessage(quote.reserveBlockReason, t)}</Alert> : null}
          <div className="open-match-v1-footer">
            <Button variant="outline-secondary" onClick={() => setStep(2)}>{t('BACK')}</Button>
            <Button className="open-match-v1-primary" disabled={creating || (quote && quote.canReserve === false)} onClick={createMatch}>
              <FaSave aria-hidden="true"/>{creating ? t('OPEN_MATCH_CREATING') : t('CREATE_MATCH')}
            </Button>
          </div>
        </section>
      ) : null}

      <Modal show={rankingModalOpen} onHide={() => setRankingModalOpen(false)} size="lg" centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('SELF_ASSESSMENT')}</span>
            <Modal.Title>{sportDisplayName(selectedSport, t)}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          {rankingSavedResult ? (
            <div className="open-match-ranking-result">
              <span>{t('OPEN_MATCH_RANKING_RESULT_LABEL')}</span>
              <strong>{rankingLevelLabel(rankingSavedResult).replace('Level ', '')}</strong>
              <p>{t('OPEN_MATCH_RANKING_RESULT_BODY_ONE', {sport: sportDisplayName(selectedSport, t)})}</p>
              <p>{t('OPEN_MATCH_RANKING_RESULT_BODY_TWO')}</p>
            </div>
          ) : (
            <Fragment>
              <div className="open-match-ranking-intro">
                <strong>{t('OPEN_MATCH_RANKING_WHY_TITLE')}</strong>
                <p>{t('OPEN_MATCH_RANKING_WHY_BODY_ONE')}</p>
                <p>{t('OPEN_MATCH_RANKING_WHY_BODY_TWO')}</p>
              </div>
              {!profileGender ? (
                <div className="player-ranking-question">
                  <strong>{t('GENDER')}</strong>
                  <small>{t('OPEN_MATCH_GENDER_REQUIRED_HELP')}</small>
                  <div className="player-ranking-options">
                    <button type="button" className={genderChoice === 'male' ? 'active' : ''} onClick={() => setGenderChoice('male')}>{t('OPEN_MATCH_GENDER_MALE')}</button>
                    <button type="button" className={genderChoice === 'female' ? 'active' : ''} onClick={() => setGenderChoice('female')}>{t('OPEN_MATCH_GENDER_FEMALE')}</button>
                  </div>
                </div>
              ) : null}
              {selectedQuestionnaire.questions.map(question => (
                <div key={question.key} className="player-ranking-question">
                  <strong>{rankingQuestionLabel(question, t)}</strong>
                  <div className="player-ranking-options">
                    {(question.options || selectedQuestionnaire.options || []).map(option => (
                      <button key={`${question.key}-${option.value}`} type="button" className={rankingAnswers[question.key] === option.value ? 'active' : ''} onClick={() => setRankingAnswers({...rankingAnswers, [question.key]: option.value})}>
                        {rankingOptionLabel(option, t)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </Fragment>
          )}
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setRankingModalOpen(false)}>{t('CLOSE')}</Button>
          {rankingSavedResult ? (
            <Button className="profile-auth-action profile-auth-action--save" onClick={() => {
              setRankingModalOpen(false);
              if (step === 1 && selectedSlotId) setStep(2);
            }}>
              {t('CONTINUE')}
            </Button>
          ) : (
            <Button className="profile-auth-action profile-auth-action--save" disabled={savingRanking} onClick={saveRanking}>
              <FaSave aria-hidden="true"/>{savingRanking ? t('SAVING') : t('SAVE_SELF_ASSESSMENT')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal show={genderModalOpen} onHide={() => setGenderModalOpen(false)} centered dialogClassName="open-match-ranking-modal">
        <Modal.Header closeButton className="open-match-ranking-modal-header">
          <div>
            <span>{t('PLAYER_PROFILE')}</span>
            <Modal.Title>{t('OPEN_MATCH_COMPLETE_THIS_INFO')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="open-match-ranking-modal-body">
          <div className="open-match-ranking-intro">
            <strong>{t('OPEN_MATCH_GENDER_MODAL_TITLE')}</strong>
            <p>{t('OPEN_MATCH_GENDER_MODAL_BODY')}</p>
          </div>
          <div className="player-ranking-options">
            <button type="button" className={genderChoice === 'male' ? 'active' : ''} onClick={() => setGenderChoice('male')}>{t('OPEN_MATCH_GENDER_MALE')}</button>
            <button type="button" className={genderChoice === 'female' ? 'active' : ''} onClick={() => setGenderChoice('female')}>{t('OPEN_MATCH_GENDER_FEMALE')}</button>
          </div>
        </Modal.Body>
        <Modal.Footer className="open-match-ranking-modal-footer">
          <Button variant="outline-secondary" className="open-match-ranking-cancel" onClick={() => setGenderModalOpen(false)}>{t('CLOSE')}</Button>
          <Button className="profile-auth-action profile-auth-action--save" disabled={savingGender} onClick={saveGenderAndContinue}>
            <FaSave aria-hidden="true"/>{savingGender ? t('SAVING') : t('SAVE_AND_CONTINUE')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

OpenMatchCreateWizard.propTypes = {
  token: PropTypes.string,
  profile: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

OpenMatchCreateWizard.defaultProps = {
  token: null,
  profile: null
};

const mapStateToProps = ({login: {token, profile}}) => ({token, profile});

export default connect(mapStateToProps)(OpenMatchCreateWizard);
